// ================================
// IMPORTS
// ================================
import { useRouter } from "vue-router"

// ================================
// TYPES
// ================================

/** Paramètres optionnels pour la redirection (ex. _id_source, _id_building, _blank). */
export type RedirectParams = Record<
    string,
    string | number | boolean | undefined
>

// ================================
// COMPOSABLE
// ================================

/**
 * Redirection via le router Vue (remplace les placeholders dans l'url).
 *
 * @returns redirectWrapper - Fonction (url, params?) pour effectuer la redirection
 *
 * @example
 * ```ts
 * const { redirectWrapper } = useRedirect()
 * redirectWrapper("/source/:_id_source/data", { _id_source: "abc" })
 * ```
 */
export function useRedirect(): {
    redirectWrapper: (url: string, params?: RedirectParams) => void
} {
    const router = useRouter()

    /**
     * Remplace les placeholders :key dans path par les valeurs de params.
     */
    function buildPath(path: string, params?: RedirectParams): string {
        if (!params) return path
        let result = path
        for (const [key, value] of Object.entries(params)) {
            if (value === undefined) continue
            result = result.replace(new RegExp(`:${key}`, "g"), String(value))
        }
        return result
    }

    /**
     * @param url - Chemin avec éventuels placeholders (ex. /source/:_id_source/data)
     * @param params - Paramètres pour remplacer les placeholders
     */
    function redirectWrapper(url: string, params?: RedirectParams): void {
        router.push(buildPath(url, params))
    }

    return { redirectWrapper }
}
